// Helpers for the contract lifecycle surfaced by GET /clients/plan/ via the
// top-level `contract_status` key (one of "active" | "expired" | "terminated").
//
// During the "expired" window the client keeps full access for a grace period
// before the backend moves them to "terminated"; these helpers derive how much
// of that window is left so the UI can prompt a renewal consistently.

// Days of full access after the contract ends. Keep in sync with the backend grace period.
export const RENEWAL_WINDOW_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseDate = (dateString) => {
  if (!dateString) return null;
  // A plain YYYY-MM-DD is parsed as UTC midnight by Date, which shifts back a day
  // in negative-offset timezones (e.g. Mexico City). Build it as a local date so the
  // day-count stays consistent with the user's local "today".
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Accept either a raw API string or an already-parsed Date.
const toDate = (value) => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  return parseDate(value);
};

export const formatFullDate = (value) => {
  const date = toDate(value);
  if (!date) return null;
  return date.toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
};

export const formatShortDate = (value) => {
  const date = toDate(value);
  if (!date) return null;
  return date.toLocaleDateString("es-MX", { day: "numeric", month: "long" });
};

/**
 * Derive the renewal window from the contract end date.
 * @returns {{ endDate: Date, deadline: Date, daysLeft: number } | null}
 *   `null` when there is no valid end date. `daysLeft` is clamped at 0.
 */
export const getRenewalInfo = (endDateString) => {
  const endDate = parseDate(endDateString);
  if (!endDate) return null;

  const deadline = new Date(endDate);
  deadline.setDate(deadline.getDate() + RENEWAL_WINDOW_DAYS);

  const daysLeft = Math.max(
    0,
    Math.round((startOfDay(deadline).getTime() - startOfDay(new Date()).getTime()) / MS_PER_DAY),
  );

  return { endDate, deadline, daysLeft };
};

/** Short, human phrase for the days left in the window, e.g. "te quedan 12 días". */
export const daysLeftPhrase = (daysLeft) =>
  daysLeft === 1 ? "te queda 1 día" : `te quedan ${daysLeft} días`;

export const isContractExpired = (contractStatus) => contractStatus === "expired";
